/**
 * Onboarding wizard — Phases 2-4 of the original portal initiative.
 *
 * The wizard is a small state machine, not a routed app: it reads the
 * invitation token from `?invite=`, probes its status, drives Microsoft
 * sign-in, validates the signed-in identity against the invitation,
 * shows the read-only company/tenant confirmation, binds the Teams
 * tenant on confirm, then offers the gated finn.zip download and lands
 * on the "you're all set" screen.
 *
 * Lives behind react-router at `/` and `/onboarding`. The chrome-free
 * `Layout` is intentional — onboarding stays focused and does not
 * carry the authenticated sidebar.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { acquireIdToken, loginRequest } from "@/auth/msal";
import {
  getInvitationStatus,
  getManifest,
  postConfirm,
  postSignIn,
} from "@/api/portal";
import { useI18n } from "@/lib/i18n";
import { Layout } from "@/components/ui";
import {
  AllSetScreen,
  ConfirmDetailsScreen,
  DownloadScreen,
  EmailMismatchScreen,
  ErrorScreen,
  FlagWrongScreen,
  LandingScreen,
  LinkProblemScreen,
  LoadingScreen,
  SignInScreen,
  TenantConflictScreen,
  type LinkProblemReason,
} from "@/pages/screens";

// The invite token is stashed here so it survives the MSAL redirect
// round-trip (which returns to the bare redirect URI, query string gone).
const INVITE_STORAGE_KEY = "finn_portal_invite";

function readInviteToken(): string | null {
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("invite");
  if (fromUrl) {
    sessionStorage.setItem(INVITE_STORAGE_KEY, fromUrl);
    // Drop the token from the visible URL; sessionStorage carries it on.
    url.searchParams.delete("invite");
    window.history.replaceState({}, "", url.pathname + url.hash);
    return fromUrl;
  }
  return sessionStorage.getItem(INVITE_STORAGE_KEY);
}

/** Save a fetched Blob to disk via a transient object-URL anchor. */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type Phase =
  | { kind: "loading"; messageKey: string }
  | { kind: "landing" }
  | { kind: "signin" }
  | {
      kind: "confirm";
      customerName: string;
      domain: string;
      signedInName: string;
    }
  | { kind: "download"; customerName: string }
  | { kind: "allset" }
  | { kind: "flag_wrong" }
  | { kind: "tenant_conflict" }
  | { kind: "mismatch"; invited: string; signed: string }
  | { kind: "link_problem"; reason: LinkProblemReason }
  | { kind: "error" };

const INITIAL: Phase = { kind: "loading", messageKey: "wizard.validating" };

export function Wizard({ authConfigured }: { authConfigured: boolean }) {
  // Before the portal AAD app exists, the wizard can't sign anyone in —
  // fall back to the static landing.
  if (!authConfigured) {
    return (
      <Layout>
        <LandingScreen />
      </Layout>
    );
  }
  return <WizardInner />;
}

function WizardInner() {
  const { instance, accounts, inProgress } = useMsal();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>(INITIAL);
  const [reloadKey, setReloadKey] = useState(0);

  const inviteToken = useMemo(() => readInviteToken(), []);
  const signedIn = accounts.length > 0;

  useEffect(() => {
    // Wait until MSAL has finished any in-flight redirect handling.
    if (inProgress !== InteractionStatus.None) return;
    if (!inviteToken) {
      // No invitation in flight: a signed-in user belongs on /account
      // (the front door for returning customers); everyone else sees
      // the public landing with a Sign-in CTA.
      if (signedIn) {
        navigate("/account", { replace: true });
      } else {
        setPhase({ kind: "landing" });
      }
      return;
    }

    let cancelled = false;
    setPhase({ kind: "loading", messageKey: "wizard.validating" });

    (async () => {
      try {
        const status = await getInvitationStatus(inviteToken);
        if (cancelled) return;

        if (status === "expired" || status === "revoked" || status === "not_found") {
          setPhase({ kind: "link_problem", reason: status });
          return;
        }
        if (status === "accepted" || status === "installed") {
          setPhase({ kind: "link_problem", reason: "already_used" });
          return;
        }

        // status === "pending" — the link is live.
        if (!signedIn) {
          setPhase({ kind: "signin" });
          return;
        }

        setPhase({ kind: "loading", messageKey: "wizard.signingIn" });
        const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
        const idToken = await acquireIdToken(instance, account);
        const result = await postSignIn(inviteToken, idToken);
        if (cancelled) return;

        switch (result.result) {
          case "ok":
            setPhase({
              kind: "confirm",
              customerName: result.customer_display_name ?? "",
              domain: result.verified_domain ?? "",
              signedInName:
                result.signed_in_name ?? result.signed_in_email ?? "",
            });
            break;
          case "email_mismatch":
            setPhase({
              kind: "mismatch",
              invited: result.invited_email ?? "",
              signed: result.signed_in_email ?? "",
            });
            break;
          case "already_used":
            setPhase({ kind: "link_problem", reason: "already_used" });
            break;
          case "expired":
          case "revoked":
          case "not_found":
            setPhase({ kind: "link_problem", reason: result.result });
            break;
        }
      } catch {
        if (!cancelled) setPhase({ kind: "error" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inProgress, inviteToken, signedIn, instance, navigate, reloadKey]);

  const onSignIn = useCallback(() => {
    instance.loginRedirect(loginRequest);
  }, [instance]);

  const onSwitchAccount = useCallback(() => {
    instance.loginRedirect({ ...loginRequest, prompt: "select_account" });
  }, [instance]);

  const onContinueToAccount = useCallback(() => {
    navigate("/account");
  }, [navigate]);

  const onRetry = useCallback(() => {
    setPhase(INITIAL);
    setReloadKey((k) => k + 1);
  }, []);

  // The confirm step is the wizard's one writing action: it binds the
  // customer's Teams tenant and accepts the invitation. The bound tenant
  // id is the verified `tid` inside the freshly re-acquired ID token —
  // never anything the user typed.
  const onConfirm = useCallback(async () => {
    if (!inviteToken) return;
    setPhase({ kind: "loading", messageKey: "wizard.confirming" });
    try {
      const account =
        instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      const idToken = await acquireIdToken(instance, account);
      const result = await postConfirm(inviteToken, idToken);
      switch (result.result) {
        case "ok":
          setPhase({
            kind: "download",
            customerName: result.customer_display_name ?? "",
          });
          break;
        case "tenant_conflict":
          setPhase({ kind: "tenant_conflict" });
          break;
        case "already_used":
          setPhase({ kind: "link_problem", reason: "already_used" });
          break;
        case "expired":
        case "revoked":
        case "not_found":
          setPhase({ kind: "link_problem", reason: result.result });
          break;
        case "email_mismatch":
          // The account changed between sign-in and confirm — rare; the
          // generic error screen lets the customer retry from scratch.
          setPhase({ kind: "error" });
          break;
      }
    } catch {
      setPhase({ kind: "error" });
    }
  }, [inviteToken, instance]);

  const onFlagWrong = useCallback(() => {
    setPhase({ kind: "flag_wrong" });
  }, []);

  // The download step fetches the gated finn.zip with the admin's
  // signed-in session and saves it to disk. Onboarding is already
  // complete server-side at this point — the download is just the
  // manifest hand-off — so this writes no state.
  const onDownload = useCallback(async () => {
    setPhase({ kind: "loading", messageKey: "wizard.downloading" });
    try {
      const account =
        instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      const idToken = await acquireIdToken(instance, account);
      const blob = await getManifest(idToken);
      triggerDownload(blob, "finn.zip");
      setPhase({ kind: "allset" });
    } catch {
      setPhase({ kind: "error" });
    }
  }, [instance]);

  function screen() {
    switch (phase.kind) {
      case "loading":
        return <LoadingScreen message={t(phase.messageKey)} />;
      case "landing":
        return <LandingScreen onSignIn={onSignIn} />;
      case "signin":
        return <SignInScreen onSignIn={onSignIn} />;
      case "confirm":
        return (
          <ConfirmDetailsScreen
            customerName={phase.customerName}
            domain={phase.domain}
            signedInName={phase.signedInName}
            onConfirm={onConfirm}
            onFlagWrong={onFlagWrong}
          />
        );
      case "download":
        return (
          <DownloadScreen
            customerName={phase.customerName}
            onDownload={onDownload}
          />
        );
      case "allset":
        return <AllSetScreen onContinue={onContinueToAccount} />;
      case "flag_wrong":
        return <FlagWrongScreen />;
      case "tenant_conflict":
        return <TenantConflictScreen />;
      case "mismatch":
        return (
          <EmailMismatchScreen
            invitedEmail={phase.invited}
            signedInEmail={phase.signed}
            onSwitchAccount={onSwitchAccount}
          />
        );
      case "link_problem":
        return <LinkProblemScreen reason={phase.reason} />;
      case "error":
        return <ErrorScreen onRetry={onRetry} />;
    }
  }

  return <Layout>{screen()}</Layout>;
}
